import React, { useState, useEffect, useCallback } from "react";
import {
    Layout, Table, Button, Form, Space, Row, Col, Breadcrumb, Modal, message,
    Typography, Select, DatePicker, Input, Descriptions
} from "antd";
import {
    HomeOutlined, AccountBookOutlined, PlusOutlined, CheckOutlined
} from "@ant-design/icons";
import api from "../../services/axiosInstance";
import dayjs from "dayjs";
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import "../../styles/fluidDesign.css";

const { Content } = Layout;
const { Option } = Select;

const CreatePayment = () => {
    const [eligible, setEligible] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [form] = Form.useForm();

    const fetchData = useCallback(async () => {
        try {
            const [eligRes, accRes] = await Promise.all([
                api.get("/expenses/payment/eligible.php"),
                api.get("/masterdropdown/listof_activepaymentaccounts.php"),
            ]);
            setEligible(eligRes.data.dataset || []);
            setAccounts(accRes.data.dataset || []);
        } catch {
            message.error("Failed to load payment data.");
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleExpenseSelect = (expId) => {
        const exp = eligible.find((e) => Number(e.exp_id) === Number(expId)) || null;
        setSelectedExpense(exp);
        if (exp) {
            const remaining = parseFloat(exp.remaining_amount);
            form.setFieldsValue({ expId: Number(exp.exp_id), paidAmount: remaining > 0 ? remaining : exp.exp_amount });
        } else {
            form.resetFields(["expId", "paidAmount"]);
        }
    };

    const handleCreate = async (values) => {
        setLoading(true);
        try {
            const res = await api.post("/expenses/payment/create.php", {
                exp_id: values.expId,
                payacc_id: values.payaccId,
                paid_amount: values.paidAmount,
                payment_release_date: values.paymentReleaseDate.format("YYYY-MM-DD"),
                payment_reference: values.paymentReference,
            });
            if (res.data.status === "success") {
                message.success(`${res.data.message} (${res.data.data?.exppay_ref})`);
                form.resetFields();
                setSelectedExpense(null);
                fetchData();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to create payment.");
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: "Expense Ref", dataIndex: "exp_ref", key: "exp_ref",
            render: (v) => <Typography.Text strong>{v}</Typography.Text>,
        },
        {
            title: "Category", dataIndex: "expcat_name", key: "expcat_name",
            render: (v) => v || "-",
        },
        {
            title: "Supplier", dataIndex: "supplier_name", key: "supplier_name",
            render: (v) => v || "-",
        },
        {
            title: "Title", dataIndex: "exp_title", key: "exp_title",
            ellipsis: true,
        },
        {
            title: "Amount", dataIndex: "exp_amount", key: "exp_amount", align: "right",
            render: (v) => <strong>LKR {parseFloat(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>,
        },
        {
            title: "Remaining", dataIndex: "remaining_amount", key: "remaining_amount", align: "right",
            render: (v) => <strong style={{ color: "#1677ff" }}>LKR {parseFloat(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>,
        },
        {
            title: "Req. Date", dataIndex: "payment_req_date", key: "payment_req_date", align: "center",
            render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
        },
        {
            title: "Action", key: "action", align: "center", width: 100,
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    icon={<CheckOutlined />}
                    disabled={selectedExpense?.exp_id === record.exp_id}
                    onClick={() => handleExpenseSelect(record.exp_id)}
                >
                    Select
                </Button>
            ),
        },
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between">
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: 'Expenses' },
                                { title: 'Create Payment' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <AccountBookOutlined /> Create Payment
                        </h1>
                    </Col>
                    <Col xs={0} md={12} style={{ textAlign: 'right' }}>
                        <p className="fluid-page-subtitle">Create payments against approved expenses</p>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <Row gutter={16}>
                    <Col xs={24} lg={10}>
                        <div className="fluid-card">
                            <div className="fluid-card-header">
                                <h3 className="fluid-card-title">Payment Details</h3>
                            </div>
                            <Form form={form} layout="vertical" onFinish={handleCreate}>
                                <Form.Item label="Approved Expense" name="expId" rules={[{ required: true, message: "Select an expense" }]}>
                                    <Select allowClear
                                        placeholder="Select approved expense"
                                        showSearch
                                        optionFilterProp="children"
                                        onChange={handleExpenseSelect}
                                    >
                                        {eligible.map((e) => (
                                            <Option key={e.exp_id} value={Number(e.exp_id)}>
                                                {e.exp_ref} - {e.exp_title}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>

                                {selectedExpense && (
                                    <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
                                        <Descriptions.Item label="Supplier">{selectedExpense.supplier_name || "-"}</Descriptions.Item>
                                        <Descriptions.Item label="Category">{selectedExpense.expcat_name || "-"}</Descriptions.Item>
                                        <Descriptions.Item label="Supplier Ref">{selectedExpense.supplier_ref || "-"}</Descriptions.Item>
                                        <Descriptions.Item label="Expense Amount">
                                            <Typography.Text strong>
                                                LKR {parseFloat(selectedExpense.exp_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </Typography.Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Total Paid">
                                            LKR {parseFloat(selectedExpense.total_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Remaining">
                                            <Typography.Text strong style={{ color: "#1677ff" }}>
                                                LKR {parseFloat(selectedExpense.remaining_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </Typography.Text>
                                        </Descriptions.Item>
                                    </Descriptions>
                                )}

                                <Form.Item label="Payment Account" name="payaccId" rules={[{ required: true, message: "Account is required" }]}>
                                    <Select allowClear placeholder="Select account" showSearch optionFilterProp="children">
                                        {accounts.map((a) => <Option key={a.value} value={Number(a.value)}>{a.label}</Option>)}
                                    </Select>
                                </Form.Item>
                                <Row gutter={[16, 0]}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item label="Paid Amount (LKR)" name="paidAmount" rules={[{ required: true, message: "Amount is required" }]}>
                                            <Input type="number" step="0.01" min="0" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item label="Release Date" name="paymentReleaseDate" rules={[{ required: true, message: "Date is required" }]}>
                                            <DatePicker style={{ width: "100%" }} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Form.Item label="Payment Reference" name="paymentReference" rules={[{ required: true, message: "Reference is required" }, { max: 100 }]}>
                                    <Input placeholder="e.g. bank transfer / cheque no." />
                                </Form.Item>
                                <Form.Item style={{ marginBottom: 0 }}>
                                    <Space>
                                        <Button danger onClick={() => { form.resetFields(); setSelectedExpense(null); }}>Clear</Button>
                                        <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading} disabled={!selectedExpense}>
                                            Create Payment
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </Form>
                        </div>
                    </Col>

                    <Col xs={24} lg={14}>
                        <div className="fluid-card">
                            <div className="fluid-card-header">
                                <h3 className="fluid-card-title">Approved Expenses Awaiting Payment</h3>
                                <Typography.Text type="secondary">{eligible.length} pending</Typography.Text>
                            </div>
                            <div className="fluid-table">
                                <Table
                                    size="small"
                                    columns={columns}
                                    dataSource={eligible}
                                    rowKey="exp_id"
                                    pagination={{
                                        defaultPageSize: 15,
                                        pageSizeOptions: [10, 50, 100, { value: 100000, label: 'All' }],
                                        showSizeChanger: true,
                                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                                    }}
                                    scroll={{ x: 750 }}
                                    locale={{ emptyText: "No approved expenses awaiting payment" }}
                                />
                            </div>
                        </div>
                    </Col>
                </Row>
            </Content>

            <Footerdiv />
        </Layout>
    );
};

export default CreatePayment;
